import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Loader2, ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContentPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageContent = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_URL}/api/site-content/page/${slug}`);
        setPageData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Page not found');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPageContent();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F4EDE4]">
        <Navbar />
        <div className="h-20" />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#D65A31]" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F4EDE4]">
        <Navbar />
        <div className="h-20" />
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12 w-full">
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold mb-2">Page Not Found</h1>
            <p className="text-gray-500 mb-6">
              The page you're looking for doesn't exist or hasn't been created yet.
            </p>
            <Button onClick={() => navigate('/')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Check if page has content
  if (!pageData.content || pageData.content.trim() === '') {
    return (
      <div className="min-h-screen flex flex-col bg-[#F4EDE4]">
        <Navbar />
        <div className="h-20" />
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12 w-full">
          <Button 
            onClick={() => navigate(-1)} 
            variant="ghost" 
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold mb-2">{pageData.title}</h1>
            <p className="text-gray-500">
              Content for this page is coming soon. Please check back later.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F4EDE4]">
      <Navbar />
      <div className="h-20" />
      
      <main className="flex-1 max-w-4xl mx-auto px-6 py-12 w-full">
        <Button 
          onClick={() => navigate(-1)} 
          variant="ghost" 
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <article className="bg-white rounded-2xl p-8 md:p-12 shadow-sm">
          <header className="mb-8 pb-6 border-b">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              {pageData.title}
            </h1>
            {pageData.section && (
              <p className="text-sm text-gray-500 mt-2 capitalize">
                {pageData.section_title || pageData.section}
              </p>
            )}
          </header>
          
          <div className="prose prose-lg max-w-none">
            {/* Render content - supporting basic formatting */}
            {pageData.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-gray-700 leading-relaxed mb-4">
                {paragraph}
              </p>
            ))}
          </div>
        </article>
      </main>
      
      <Footer />
    </div>
  );
};

export default ContentPage;
